import COMMON_CATEGORIES from 'config/constants/common-categories';
import { EffectiveCart } from 'interfaces/cart';
import { FormattedShopifyCheckModel } from 'interfaces/checkout';
import { isSubscriptionItem, PdProductItem } from 'interfaces/line-item';

/**
 * if excludingTrial is false, probierpaket is not counted
 * @param items
 * @param excludingTrials
 * @returns
 */
export const getFrozenProductsCount = (
  items: FormattedShopifyCheckModel['items'],
  excludingTrials = true
): number =>
  count(
    items.map((item) => ({
      ...item.variant.product,
      quantity: item.quantity,
      tags: item.variant.product.tags.toString(),
    })),
    excludingTrials
  );

export const countFrozenItems = (
  items: Array<Pick<PdProductItem, 'tags' | 'productType' | 'quantity'>>,
  excludingTrials = true
): number => count(items, excludingTrials);

const count = (
  items: Array<{
    productType: string;
    tags: string;
    quantity: number;
  }>,
  excludingTrials: boolean
): number =>
  items.reduce((count, item) => {
    const { productType, tags } = item;

    const tagToTest = new RegExp(`${COMMON_CATEGORIES.barfTastingPacks}?`);

    const isTrialProduct = tagToTest.test(tags);

    if (
      tags.includes(COMMON_CATEGORIES.notMov) ||
      productType !== COMMON_CATEGORIES.barf ||
      (excludingTrials && isTrialProduct)
    ) {
      return count;
    }

    if (tags.includes(COMMON_CATEGORIES.bundle)) {
      return count + 10;
    }

    return count + item.quantity;
  }, 0);

/**
 * Returns the coupon code for variant pricing display on CDP product cards.
 * Only returns the coupon code for existing customers, undefined otherwise.
 */
export const getVariantCouponCode = (
  isEc: boolean,
  couponCode: string | undefined
): string | undefined => (isEc ? couponCode || undefined : undefined);

/**
 * Determines if express checkout is available based on shop, cart contents, and discount type
 * Express checkout is hidden when user has a savings plan (either in cart or active customer plan)
 */
export const isExpressCheckoutAvailable = (
  isExpressCheckoutEnabled: boolean,
  effectiveCart: EffectiveCart | null | undefined,
  hasSavingsPlan: boolean
): boolean => {
  if (!effectiveCart) return false;

  const containsSubscriptionItem = effectiveCart.items.some((item) => isSubscriptionItem(item));
  const containsBundle = effectiveCart.items.some(
    (item) => Array.isArray(item.bundleRefs) && item.bundleRefs.length > 0
  );

  const discountType = effectiveCart.discount?.type;

  return (
    isExpressCheckoutEnabled &&
    !containsSubscriptionItem &&
    discountType !== 'Credit' &&
    !hasSavingsPlan &&
    !containsBundle
  );
};
